import { useCallback, useState } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext, RenderContext } from "../core/render-context"
import { ElementRenderer } from "./element-renderer"
import { Badge } from "../components/ui/badge"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table"
import { Checkbox } from "../components/ui/checkbox"
import { cn } from "../lib/utils"
import { resolveIcon } from "./resolve-icon"
import { Loader2, Search, AlertCircle } from "lucide-react"
import type { DataTableColumn } from "@cogo/types"

const numberFormatter = new Intl.NumberFormat("en-US")

/** Format compact date strings like "20260306062450" → "2026/03/06" or ISO dates */
function formatCompactDate(raw: string): string {
  // Compact format: YYYYMMDDHHmmss (14 chars) or YYYYMMDD (8 chars)
  if (/^\d{8,14}$/.test(raw)) {
    const y = raw.slice(0, 4)
    const m = raw.slice(4, 6)
    const d = raw.slice(6, 8)
    return `${y}/${m}/${d}`
  }
  // ISO format: try parsing
  const parsed = new Date(raw)
  if (!isNaN(parsed.getTime())) {
    return `${parsed.getFullYear()}/${String(parsed.getMonth() + 1).padStart(2, "0")}/${String(parsed.getDate()).padStart(2, "0")}`
  }
  return raw
}

export function DataTableRenderer({ element }: RendererProps) {
  const ctx = useRenderContext()
  const { resolveSymbol, dispatchEvent } = ctx
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set())

  // Resolve data array
  const rawData = element.data ? resolveSymbol(element.data as string) : []
  const allItems: Record<string, unknown>[] = Array.isArray(rawData) ? rawData : []

  // Client-side pagination: slice data by pageIndex/pageSize if provided
  const pageIndex = element.pageIndex != null
    ? Number(typeof element.pageIndex === "string" ? resolveSymbol(element.pageIndex) : element.pageIndex) || 0
    : undefined
  const pageSize = element.pageSize != null
    ? Number(typeof element.pageSize === "string" ? resolveSymbol(element.pageSize) : element.pageSize) || 20
    : undefined
  const items = (pageIndex != null && pageSize != null)
    ? allItems.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize)
    : allItems

  // Resolve loading state
  const isLoading = typeof element.loading === "string"
    ? !!resolveSymbol(element.loading)
    : !!element.loading

  // Resolve error state
  const errorMessage = element.error
    ? String(resolveSymbol(element.error) ?? "")
    : ""

  const columns: DataTableColumn[] = (element.columns as DataTableColumn[]) ?? []

  // Configurable state heights from JSON (default: h-64 for loading/error, h-48 for empty)
  const stateHeight = (element.stateHeight as string) ?? "h-64"
  const emptyHeight = (element.emptyHeight as string) ?? "h-48"

  // Row click handler
  const handleRowClick = useCallback(
    (rowIndex: number, rowData: Record<string, unknown>) => {
      dispatchEvent(element.id, "rowClick", { rowIndex, rowData })
    },
    [element.id, dispatchEvent],
  )

  // Selection toggle
  const toggleRow = useCallback((rowIndex: number) => {
    setSelectedRows((prev) => {
      const next = new Set(prev)
      if (next.has(rowIndex)) next.delete(rowIndex)
      else next.add(rowIndex)
      return next
    })
  }, [])

  const toggleAll = useCallback(() => {
    setSelectedRows((prev) =>
      prev.size === items.length ? new Set() : new Set(items.map((_, i) => i)),
    )
  }, [items.length])

  // Alignment class
  const alignClass = (align?: string) =>
    align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left"

  // Render cell by column type
  const renderCell = (col: DataTableColumn, item: Record<string, unknown>, rowIndex: number) => {
    const value = item[col.key]
    const displayValue = value != null ? String(value) : ""

    // Apply formatting based on column format
    let formattedValue = displayValue
    if (col.format === "number" && value != null && !isNaN(Number(value))) {
      formattedValue = numberFormatter.format(Number(value))
    } else if (col.format === "date" && displayValue) {
      formattedValue = formatCompactDate(displayValue)
    }

    switch (col.type) {
      case "text":
        return <span className={cn("text-[13px]", col.className)}>{formattedValue}</span>

      case "badge": {
        const variant = col.variantMap?.[displayValue] ?? col.variantMap?.["default"] ?? "default"
        const label = col.labelMap?.[displayValue] ?? displayValue
        return (
          <Badge
            variant={variant as "default" | "secondary" | "destructive" | "outline"}
            className={col.className}
          >
            {label || "—"}
          </Badge>
        )
      }

      case "checkbox":
        return (
          <Checkbox
            checked={selectedRows.has(rowIndex)}
            onCheckedChange={() => toggleRow(rowIndex)}
          />
        )

      case "button": {
        const IconComp = col.icon ? resolveIcon(col.icon) : undefined
        const isLink = col.buttonVariant === "link"
        return (
          <button
            className={cn(
              "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors",
              col.buttonVariant === "ghost" && "hover:bg-muted/50 h-8 w-8 p-0",
              col.buttonVariant === "outline" && "border border-border hover:bg-muted/50 h-8 px-3",
              isLink && "underline-offset-4 hover:underline",
              !col.buttonVariant && "bg-primary text-primary-foreground hover:bg-primary/90 h-8 px-3",
              col.className,
            )}
            onClick={(e) => {
              e.stopPropagation()
              dispatchEvent(element.id, "cellClick", {
                rowIndex,
                columnKey: col.key,
                value,
              })
            }}
          >
            {IconComp && <IconComp className="h-4 w-4" />}
            {displayValue}
          </button>
        )
      }

      case "icon": {
        const IconComp = col.icon ? resolveIcon(col.icon) : undefined
        return IconComp ? <IconComp className={cn("h-4 w-4", col.className)} /> : null
      }

      case "custom":
        if (col.template) {
          const childCtx = {
            ...ctx,
            itemContext: { ...ctx.itemContext, item: { ...item, _index: rowIndex } },
          }
          return (
            <RenderContext.Provider value={childCtx}>
              <ElementRenderer element={col.template} />
            </RenderContext.Provider>
          )
        }
        return <span>{formattedValue}</span>

      default:
        return <span>{formattedValue}</span>
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div
        className={cn("w-full overflow-auto rounded-lg border border-border bg-card shadow-sm", element.className)}
        data-testid={`data-table-${element.id}`}
      >
        <Table className="border-separate border-spacing-0">
          <TableHeader>
            <TableRow>
              <TableCell
                colSpan={columns.length + (element.selectable ? 1 : 0)}
                className={cn(stateHeight, "text-center")}
              >
                <div className="flex flex-col items-center justify-center gap-3">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  <span className="text-[13px] text-muted-foreground italic">Loading...</span>
                </div>
              </TableCell>
            </TableRow>
          </TableHeader>
        </Table>
      </div>
    )
  }

  // Error state
  if (errorMessage) {
    return (
      <div
        className={cn("w-full overflow-auto rounded-lg border border-border bg-card shadow-sm", element.className)}
        data-testid={`data-table-${element.id}`}
      >
        <Table className="border-separate border-spacing-0">
          <TableBody>
            <TableRow>
              <TableCell
                colSpan={columns.length + (element.selectable ? 1 : 0)}
                className={cn(stateHeight, "text-center")}
              >
                <div className="flex flex-col items-center justify-center gap-2 text-destructive">
                  <AlertCircle className="h-8 w-8 mb-2" />
                  <span className="text-sm font-medium">{errorMessage}</span>
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    )
  }

  // Empty state — still render headers for structural parity with Next.js
  if (items.length === 0) {
    return (
      <div
        className={cn("w-full overflow-auto rounded-lg border border-border bg-card shadow-sm", element.className)}
        data-testid={`data-table-${element.id}`}
      >
        <Table className="border-separate border-spacing-0">
          <TableHeader>
            <TableRow className="h-10 hover:bg-transparent">
              {element.selectable && (
                <TableHead className="w-10">
                  <Checkbox checked={false} disabled />
                </TableHead>
              )}
              {columns.map((col) => (
                <TableHead
                  key={col.key}
                  className={cn(
                    "text-[11px] font-bold uppercase tracking-widest",
                    alignClass(col.align),
                    col.headerClassName,
                  )}
                  style={col.width ? { width: col.width } : undefined}
                >
                  {col.header ?? col.label ?? ""}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell
                colSpan={columns.length + (element.selectable ? 1 : 0)}
                className={cn(emptyHeight, "text-center")}
              >
                <div className="flex flex-col items-center justify-center gap-2">
                  <Search className="h-10 w-10 text-muted-foreground/30 mb-2" />
                  <span className="text-[13px] font-medium text-muted-foreground">
                    {element.emptyTitle ?? "No results"}
                  </span>
                  {element.emptySub && (
                    <span className="text-[12px] text-muted-foreground/70">
                      {element.emptySub}
                    </span>
                  )}
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    )
  }

  // Data table
  return (
    <div
      className={cn("w-full overflow-auto rounded-lg border border-border bg-card shadow-sm", element.className)}
      data-testid={`data-table-${element.id}`}
    >
      <Table className="border-separate border-spacing-0">
        <TableHeader
          className={cn(
            element.stickyHeader && "sticky top-0 z-10 bg-muted/80 backdrop-blur-sm",
          )}
        >
          <TableRow className="h-10 hover:bg-transparent">
            {element.selectable && (
              <TableHead className="w-10">
                <Checkbox
                  checked={selectedRows.size === items.length && items.length > 0}
                  onCheckedChange={toggleAll}
                />
              </TableHead>
            )}
            {columns.map((col) => (
              <TableHead
                key={col.key}
                className={cn(
                  "text-[11px] font-bold uppercase tracking-widest",
                  alignClass(col.align),
                  col.headerClassName,
                )}
                style={col.width ? { width: col.width } : undefined}
              >
                {col.header ?? col.label ?? ""}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody
          className={cn(
            element.striped && "striped [&>tr:nth-child(even)]:bg-muted/30",
          )}
        >
          {items.map((item, rowIndex) => (
            <TableRow
              key={`${String(item.id ?? '')}-${rowIndex}`}
              className={cn(
                "h-12 cursor-pointer",
                selectedRows.has(rowIndex) && "bg-muted",
              )}
              data-state={selectedRows.has(rowIndex) ? "selected" : undefined}
              onClick={() => handleRowClick(rowIndex, item)}
            >
              {element.selectable && (
                <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedRows.has(rowIndex)}
                    onCheckedChange={() => toggleRow(rowIndex)}
                  />
                </TableCell>
              )}
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  className={alignClass(col.align)}
                >
                  {renderCell(col, item, rowIndex)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
